import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Tab,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Skeleton,
  Theme,
} from '@mui/material';
import { useTheme, type SxProps } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from 'tss-react/mui';
import { getStyleVariables, menuLightGrey } from 'styles/vars';
import cx from 'classnames';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import {
  ProfileFields,
  parseIniToProfileFields,
  defaultGraphicFields,
} from 'pages/MyAccount/customProfiles/profileFormatter';
import { EditorChildProps } from './types';

const GIF_BASE = '/gifs/profile-editor/';
const GIF_PREVIEW_MAX_HEIGHT_PX = 190;
const GIF_PANEL_WIDTH_PX = 240;
const GIF_PANEL_ALIGN_SPACING_LOW = 9;
const GIF_PANEL_ALIGN_SPACING_HIGH = 12;
const CONTENT_PAPER_HEIGHT_PX = 352;

const FIELD_GIF_MAP: Partial<Record<keyof ProfileFields, string>> = {
  DiffGrouping: 'general_diff_grouping',
  MatchPer: 'general_similarity_shapes',
  MatchVar: 'general_similarity_content',
  CleanEdgePixel: 'general_ignore_edges',
  MaskDielinePixel: 'general_mask_creases',
  ContentText_MinDiffDia: 'text_min_detection',
  ContentText_Sensitivity: 'text_sensitivity',
  ContentText_Smoothing: 'text_noise_correction',
  ContentText_Refine: 'text_kerning_correction',
  ArtworkText_MinDiffDia: 'text_min_detection',
  ArtworkText_Sensitivity: 'text_sensitivity',
  ArtworkText_Smoothing: 'text_noise_correction',
  ArtworkText_Refine: 'text_kerning_correction',
  Content_MinDiffDia: 'artwork_min_detection',
  Content_MaxDiffShift: 'artwork_image_shifting',
  Content_Sensitivity: 'artwork_sensitivity',
  Content_Smoothing: 'artwork_noise_correction',
  Artwork_MinDiffDia: 'artwork_min_detection',
  Artwork_MaxDiffShift: 'artwork_image_shifting',
  Artwork_Sensitivity: 'artwork_sensitivity',
  Artwork_Smoothing: 'artwork_noise_correction',
};

const defaultFields = defaultGraphicFields;

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      width: '100%',
    },
    body: {
      display: 'flex',
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      gap: theme.spacing(2),
    },
    contentPaper: {
      flex: '0 0 auto',
      height: CONTENT_PAPER_HEIGHT_PX,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '4px',
      padding: theme.spacing(2, 3),
      marginTop: theme.spacing(1),
      backgroundColor: menuLightGrey,
    },
    formArea: {
      flex: 1,
      minWidth: 0,
      width: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    formAreaScroll: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflow: 'auto',
      boxSizing: 'border-box',
      paddingLeft: theme.spacing(2),
      paddingRight: 0,
      ...styleVariables.scrollBarThin,
      scrollbarGutter: 'auto',
    },
    generalBodyFlex: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      minWidth: 0,
      minHeight: 0,
      width: '100%',
      alignItems: 'flex-start',
      transition: 'gap 0.3s ease',
    },
    formAreaGeneralColumn: {
      flex: 1,
      minWidth: 0,
      width: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    formAreaGeneralColumnScroll: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflow: 'auto',
      boxSizing: 'border-box',
      paddingRight: theme.spacing(0.5),
      ...styleVariables.scrollBarThin,
      scrollbarGutter: 'auto',
    },
    gifPanelGeneralColumn: {
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      overflow: 'hidden',
      transition: 'width 0.3s ease, opacity 0.3s ease',
    },
    generalCheckboxGrid: {
      paddingLeft: theme.spacing(2),
      boxSizing: 'border-box',
    },
    gifPanel: {
      overflow: 'hidden',
      transition: 'width 0.3s ease, opacity 0.3s ease',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      alignSelf: 'flex-start',
    },
    gifImage: {
      width: '100%',
      maxHeight: GIF_PREVIEW_MAX_HEIGHT_PX,
      height: 'auto',
      objectFit: 'contain',
      borderRadius: '8px',
      display: 'block',
    },
    accordion: {
      background: 'transparent',
      boxShadow: 'none',
      '&:before': { display: 'none' },
      '&.Mui-expanded': { margin: 0 },
    },
    accordionSummaryFormTabs: {
      padding: 0,
      minHeight: 'unset',
      '&.Mui-expanded': { minHeight: 'unset' },
      '& .MuiAccordionSummary-content': {
        margin: '4px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flex: 1,
        justifyContent: 'flex-start',
      },
      '& .MuiAccordionSummary-content.Mui-expanded': { margin: '4px 0' },
    },
    expandIcon: {
      color: '#fff',
      fontSize: '1.1rem',
      transition: 'transform 0.2s',
    },
    expandIconOpen: {
      transform: 'rotate(180deg)',
    },
    accordionDetailsTextArtwork: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      paddingLeft: 0,
      paddingRight: 0,
      overflow: 'visible',
    },
    colorDetectionInset: {
      padding: 0,
      boxSizing: 'border-box',
    },
  };
});

function isNumericError(value: string, min: number, max?: number): boolean {
  if (value === '' || value == null) return true;
  const n = Number(value);
  if (isNaN(n)) return true;
  if (n < min) return true;
  if (max !== undefined && n > max) return true;
  return false;
}

function computeHasErrors(f: ProfileFields): boolean {
  return (
    isNumericError(f.DiffGrouping, 0, 999) ||
    isNumericError(f.MatchPer, 5, 99) ||
    isNumericError(f.MatchVar, 5, 99) ||
    isNumericError(f.CleanEdgePixel, -125, 125) ||
    isNumericError(f.MaskDielinePixel, 0, 125) ||
    isNumericError(f.ContentText_MinDiffDia, 0) ||
    isNumericError(f.ContentText_MaxDiffShift, 0) ||
    isNumericError(f.ContentText_Sensitivity, 1, 254) ||
    isNumericError(f.ContentText_Smoothing, 0) ||
    isNumericError(f.ContentText_Refine, 0) ||
    isNumericError(f.ArtworkText_MinDiffDia, 0) ||
    isNumericError(f.ArtworkText_MaxDiffShift, 0) ||
    isNumericError(f.ArtworkText_Sensitivity, 1, 254) ||
    isNumericError(f.ArtworkText_Smoothing, 0) ||
    isNumericError(f.ArtworkText_Refine, 0) ||
    isNumericError(f.Content_MinDiffDia, 0) ||
    isNumericError(f.Content_MaxDiffShift, 0) ||
    isNumericError(f.Content_Sensitivity, 1, 254) ||
    isNumericError(f.Content_Smoothing, 0) ||
    isNumericError(f.Content_Refine, 0) ||
    isNumericError(f.Artwork_MinDiffDia, 0) ||
    isNumericError(f.Artwork_MaxDiffShift, 0) ||
    isNumericError(f.Artwork_Sensitivity, 1, 254) ||
    isNumericError(f.Artwork_Smoothing, 0) ||
    isNumericError(f.Artwork_Refine, 0) ||
    isNumericError(f.Color_Min, 0) ||
    isNumericError(f.Color_Mean, 0, 225)
  );
}

type GraphicEditorProps = EditorChildProps<ProfileFields>;

const GraphicProfileEditor: React.FC<GraphicEditorProps> = ({ profile, onFieldsChange, onValidChange }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [fields, setFields] = useState<ProfileFields>(defaultFields);
  const [focusedField, setFocusedField] = useState<keyof ProfileFields | null>(null);
  const [textExpanded, setTextExpanded] = useState<'low' | 'high' | null>('low');
  const [artworkExpanded, setArtworkExpanded] = useState<'low' | 'high' | null>('low');

  useEffect(() => {
    if (profile?.customProfileString) {
      setFields(parseIniToProfileFields(profile.customProfileString));
    } else {
      setFields(defaultFields);
    }
    setTab(0);
  }, [profile]);

  useEffect(() => {
    onFieldsChange(fields);
    onValidChange(computeHasErrors(fields));
  }, [fields, onFieldsChange, onValidChange]);

  const set = (key: keyof ProfileFields, val: string | boolean) => setFields((prev) => ({ ...prev, [key]: val }));

  const blurToDefault = (key: keyof ProfileFields, def: string) =>
    setFields((prev) => ({
      ...prev,
      [key]: (prev[key] as string).trim() === '' ? def : prev[key],
    }));

  const activeGif = focusedField ? (FIELD_GIF_MAP[focusedField] ?? null) : null;
  const textArtworkExpanded = tab === 1 ? textExpanded : tab === 2 ? artworkExpanded : null;
  const textArtworkGifMarginTop =
    activeGif && (tab === 1 || tab === 2)
      ? textArtworkExpanded === 'high'
        ? theme.spacing(GIF_PANEL_ALIGN_SPACING_HIGH)
        : textArtworkExpanded === 'low'
          ? theme.spacing(GIF_PANEL_ALIGN_SPACING_LOW)
          : 0
      : 0;
  const [gifLoaded, setGifLoaded] = useState(false);

  useEffect(() => {
    if (activeGif) setGifLoaded(false);
  }, [activeGif]);

  const handleGifLoad = useCallback(() => setGifLoaded(true), []);

  const focus = (key: keyof ProfileFields) => () => setFocusedField(key);
  const blur = (key: keyof ProfileFields, def?: string) => () => {
    setFocusedField(null);
    if (def !== undefined) blurToDefault(key, def);
  };

  const helper = (key: keyof ProfileFields, text: string) => (focusedField === key ? text : undefined);

  const numField = (
    key: keyof ProfileFields,
    label: string,
    min: number,
    max: number | undefined,
    def: string,
    helperText: string,
    testId: string,
    xs?: 4 | 6 | 12,
    gridItemSx?: SxProps<Theme>,
  ) => (
    <Grid item xs={xs ?? 6} sx={gridItemSx}>
      <GVTextField
        label={label}
        value={fields[key] as string}
        onValueChange={(value) => set(key, value ?? '')}
        onFocus={focus(key)}
        onBlur={blur(key, def)}
        message={helper(key, helperText)}
        hasError={isNumericError(fields[key] as string, min, max)}
        data-testid={testId}
      />
    </Grid>
  );

  const checkField = (
    key: keyof ProfileFields,
    label: string,
    testId: string,
    xs?: 4 | 6 | 12,
    gridItemClassName?: string,
    gridItemSx?: SxProps<Theme>,
  ) => (
    <Grid item xs={xs ?? 12} className={gridItemClassName} sx={gridItemSx}>
      <GVCheckbox
        checked={fields[key] as boolean}
        onChange={(e) => set(key, e.target.checked)}
        label={label}
        data-testid={testId}
      />
    </Grid>
  );

  const gifPanelInner =
    activeGif != null ? (
      <>
        {!gifLoaded && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={GIF_PREVIEW_MAX_HEIGHT_PX}
            sx={{ borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.08)' }}
          />
        )}
        <img
          src={`${GIF_BASE}${activeGif}.gif`}
          data-testid="gif-panel-image"
          alt={activeGif.replace(/_/g, ' ')}
          className={classes.gifImage}
          onLoad={handleGifLoad}
          style={{ display: gifLoaded ? 'block' : 'none' }}
        />
      </>
    ) : null;

  const generalNumericRowGapAfterDiff = { mt: 1 } satisfies SxProps<Theme>;

  const generalFieldGrids = (
    <>
      <Grid container spacing={1}>
        {numField(
          'DiffGrouping',
          'Difference grouping (pixels)',
          0,
          999,
          '50',
          'Range: 0–999, Default: 50',
          'difference-grouping-pixels',
          12,
        )}
        {numField(
          'MatchPer',
          'Similarity (find repeating shapes)',
          5,
          99,
          '35',
          'Range: 5–99, Default: 35',
          'similarity-find-repeating-shapes',
          6,
          generalNumericRowGapAfterDiff,
        )}
        {numField(
          'MatchVar',
          'Similarity (find repeating content)',
          5,
          99,
          '85',
          'Range: 5–99, Default: 85',
          'similarity-find-repeating-content',
          6,
          generalNumericRowGapAfterDiff,
        )}
        {numField(
          'CleanEdgePixel',
          'Ignore edges (pixels)',
          -125,
          125,
          '0',
          'Range: −125 to 125, Default: 0',
          'ignore-edges-pixels',
        )}
        {numField(
          'MaskDielinePixel',
          'Mask creases (pixels)',
          0,
          125,
          '0',
          'Range: 0–125, Default: 0',
          'mask-creases-pixels',
        )}
      </Grid>
      <div className={classes.generalCheckboxGrid}>
        <Grid container spacing={1}>
          {checkField(
            'AutoDistort',
            'Correct distortion, rotation and skew',
            'correct-distortion-rotation-and-skew',
            12,
          )}
          {checkField('PaddSample', 'Image padding', 'padd-sample-checkbox', 12)}
        </Grid>
      </div>
    </>
  );

  const textFieldGrids = (
    <div>
      <Accordion
        expanded={textExpanded === 'low'}
        onChange={(_, isExpanded) => setTextExpanded(isExpanded ? 'low' : null)}
        className={classes.accordion}
      >
        <AccordionSummary className={classes.accordionSummaryFormTabs}>
          <Typography variant="subtitle2">Text – Low Contrast</Typography>
          <ExpandMoreIcon className={cx(classes.expandIcon, { [classes.expandIconOpen]: textExpanded === 'low' })} />
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetailsTextArtwork}>
          <Grid container spacing={1}>
            {checkField('CheckContentText', 'Enable Low Contrast Text Detection', 'enable-low-contrast-text-detection')}
            {numField(
              'ContentText_MinDiffDia',
              'Minimum detection size (pixels)',
              0,
              Infinity,
              '1',
              'Min: 0, Default: 1',
              'minimum-detection-size-pixels-text-low-contrast',
              6,
            )}
            {numField(
              'ContentText_MaxDiffShift',
              'Maximum difference shift (pixels)',
              0,
              Infinity,
              '0',
              'Min: 0, Default: 0',
              'maximum-difference-shift-pixels-text-low-contrast',
              6,
            )}
            {numField(
              'ContentText_Sensitivity',
              'Sensitivity',
              1,
              254,
              '25',
              'Range: 1-254, Default: 25',
              'sensitivity-text-low-contrast',
              12,
            )}
            {numField(
              'ContentText_Smoothing',
              'Correction level (reduce noise)',
              0,
              Infinity,
              '1',
              'Min: 0, Default: 1',
              'correction-level-reduce-noise-text-low-contrast',
              6,
            )}
            {numField(
              'ContentText_Refine',
              'Correction level (correct kerning)',
              0,
              Infinity,
              '0',
              'Min: 0, Default: 0',
              'correction-level-correct-kerning-text-low-contrast',
              6,
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={textExpanded === 'high'}
        onChange={(_, isExpanded) => setTextExpanded(isExpanded ? 'high' : null)}
        className={classes.accordion}
      >
        <AccordionSummary className={classes.accordionSummaryFormTabs}>
          <Typography variant="subtitle2">Text – High Contrast</Typography>
          <ExpandMoreIcon className={cx(classes.expandIcon, { [classes.expandIconOpen]: textExpanded === 'high' })} />
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetailsTextArtwork}>
          <Grid container spacing={1}>
            {checkField(
              'CheckArtWorkText',
              'Enable High Contrast Text Detection',
              'enable-high-contrast-text-detection',
            )}
            {numField(
              'ArtworkText_MinDiffDia',
              'Minimum detection size (pixels)',
              0,
              Infinity,
              '1',
              'Min: 0, Default: 1',
              'minimum-detection-size-pixels-text-high-contrast',
              6,
            )}
            {numField(
              'ArtworkText_MaxDiffShift',
              'Maximum difference shift (pixels)',
              0,
              Infinity,
              '0',
              'Min: 0, Default: 0',
              'maximum-difference-shift-pixels-text-high-contrast',
              6,
            )}
            {numField(
              'ArtworkText_Sensitivity',
              'Sensitivity',
              1,
              254,
              '160',
              'Range: 1-254, Default: 160',
              'sensitivity-text-high-contrast',
              12,
            )}
            {numField(
              'ArtworkText_Smoothing',
              'Correction level (reduce noise)',
              0,
              Infinity,
              '5',
              'Min: 0, Default: 5',
              'correction-level-reduce-noise-text-high-contrast',
              6,
            )}
            {numField(
              'ArtworkText_Refine',
              'Correction level (correct kerning)',
              0,
              Infinity,
              '5',
              'Min: 0, Default: 5',
              'correction-level-correct-kerning-text-high-contrast',
              6,
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );

  const artworkFieldGrids = (
    <div>
      <Accordion
        expanded={artworkExpanded === 'low'}
        onChange={(_, isExpanded) => setArtworkExpanded(isExpanded ? 'low' : null)}
        className={classes.accordion}
      >
        <AccordionSummary className={classes.accordionSummaryFormTabs}>
          <Typography variant="subtitle2">Artwork – Low Contrast</Typography>
          <ExpandMoreIcon className={cx(classes.expandIcon, { [classes.expandIconOpen]: artworkExpanded === 'low' })} />
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetailsTextArtwork}>
          <Grid container spacing={1}>
            {checkField(
              'CheckContent',
              'Enable Low Contrast Artwork Detection',
              'enable-low-contrast-artwork-detection',
            )}
            {numField(
              'Content_MinDiffDia',
              'Minimum detection size (pixels)',
              0,
              Infinity,
              '10',
              'Min: 0, Default: 10',
              'minimum-detection-size-pixels-artwork-low-contrast',
              6,
            )}
            {numField(
              'Content_MaxDiffShift',
              'Maximum difference shift (pixels)',
              0,
              Infinity,
              '0',
              'Min: 0, Default: 0',
              'maximum-difference-shift-pixels-artwork-low-contrast',
              6,
            )}
            {numField(
              'Content_Sensitivity',
              'Sensitivity',
              1,
              254,
              '40',
              'Range: 1–254, Default: 40',
              'sensitivity-artwork-low-contrast',
              12,
            )}
            {numField(
              'Content_Smoothing',
              'Correction level (reduce noise)',
              0,
              Infinity,
              '2',
              'Min: 0, Default: 2',
              'correction-level-reduce-noise-artwork-low-contrast',
              6,
            )}
            {numField(
              'Content_Refine',
              'Correction level (correct kerning)',
              0,
              Infinity,
              '2',
              'Min: 0, Default: 2',
              'correction-level-correct-kerning-artwork-low-contrast',
              6,
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={artworkExpanded === 'high'}
        onChange={(_, isExpanded) => setArtworkExpanded(isExpanded ? 'high' : null)}
        className={classes.accordion}
      >
        <AccordionSummary className={classes.accordionSummaryFormTabs}>
          <Typography variant="subtitle2">Artwork – High Contrast</Typography>
          <ExpandMoreIcon
            className={cx(classes.expandIcon, { [classes.expandIconOpen]: artworkExpanded === 'high' })}
          />
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetailsTextArtwork}>
          <Grid container spacing={1}>
            {checkField(
              'CheckArtwork',
              'Enable High Contrast Artwork Detection',
              'enable-high-contrast-artwork-detection',
            )}
            {numField(
              'Artwork_MinDiffDia',
              'Minimum detection size (pixels)',
              0,
              Infinity,
              '10',
              'Min: 0, Default: 10',
              'minimum-detection-size-pixels-artwork-high-contrast',
              6,
            )}
            {numField(
              'Artwork_MaxDiffShift',
              'Maximum difference shift (pixels)',
              0,
              Infinity,
              '0',
              'Min: 0, Default: 0',
              'maximum-difference-shift-pixels-artwork-high-contrast',
              6,
            )}
            {numField(
              'Artwork_Sensitivity',
              'Sensitivity',
              1,
              254,
              '40',
              'Range: 1–254, Default: 40',
              'sensitivity-artwork-high-contrast',
              12,
            )}
            {numField(
              'Artwork_Smoothing',
              'Correction level (reduce noise)',
              0,
              Infinity,
              '2',
              'Min: 0, Default: 2',
              'correction-level-reduce-noise-artwork-high-contrast',
              6,
            )}
            {numField(
              'Artwork_Refine',
              'Correction level (correct kerning)',
              0,
              Infinity,
              '2',
              'Min: 0, Default: 2',
              'correction-level-correct-kerning-artwork-high-contrast',
              6,
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );

  const colorFieldGrids = (
    <div className={classes.colorDetectionInset}>
      <Grid container spacing={1}>
        {checkField('Color_Enabled', 'Enhanced color detection (digital)', 'enhanced-color-detection-digital')}
        {numField(
          'Color_Min',
          'Minimum detection size (pixels)',
          0,
          Infinity,
          '1000',
          'Min: 0, Default: 1000',
          'minimum-detection-size-pixels-color',
        )}
        {numField('Color_Mean', 'Sensitivity', 0, 225, '120', 'Range: 0–225, Default: 120', 'sensitivity-color')}
      </Grid>
    </div>
  );

  return (
    <div className={classes.root}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="inherit" indicatorColor="secondary">
        <Tab label="General" />
        <Tab label="Text" />
        <Tab label="Artwork" />
        <Tab label="Color Detection" />
      </Tabs>

      <Paper elevation={0} className={classes.contentPaper}>
        <div className={classes.body}>
          {tab === 0 ? (
            <div className={classes.generalBodyFlex} style={{ gap: activeGif ? theme.spacing(1.5) : 0 }}>
              <div className={classes.formAreaGeneralColumn}>
                <div className={classes.formAreaGeneralColumnScroll}>{generalFieldGrids}</div>
              </div>
              <div
                className={classes.gifPanelGeneralColumn}
                style={{
                  width: activeGif ? GIF_PANEL_WIDTH_PX : 0,
                  opacity: activeGif ? 1 : 0,
                  pointerEvents: activeGif ? 'auto' : 'none',
                }}
              >
                {gifPanelInner}
              </div>
            </div>
          ) : (
            <>
              <div className={classes.formArea}>
                <div className={classes.formAreaScroll}>
                  {tab === 1 && textFieldGrids}
                  {tab === 2 && artworkFieldGrids}
                  {tab === 3 && colorFieldGrids}
                </div>
              </div>

              {/* GIF panel — slides in when a mapped field is focused */}
              <div
                className={classes.gifPanel}
                style={{
                  width: activeGif ? `${GIF_PANEL_WIDTH_PX}px` : '0px',
                  opacity: activeGif ? 1 : 0,
                  marginTop: textArtworkGifMarginTop,
                }}
              >
                {gifPanelInner}
              </div>
            </>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default GraphicProfileEditor;
